import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    city: "",
    state: "",
    income_band: "",
    pay_frequency: "",
    typical_bills: []
  });
  const [loading, setLoading] = useState(false);

  const billOptions = [
    { id: "rent", label: "Rent/Mortgage" },
    { id: "utilities", label: "Utilities (Electric, Gas, Water)" },
    { id: "phone", label: "Phone/Internet" },
    { id: "car", label: "Car Payment/Insurance" },
    { id: "groceries", label: "Groceries" },
    { id: "other", label: "Other Essential Bills" }
  ];

  const handleBillToggle = (billId) => {
    setFormData(prev => ({
      ...prev,
      typical_bills: prev.typical_bills.includes(billId)
        ? prev.typical_bills.filter(b => b !== billId)
        : [...prev.typical_bills, billId]
    }));
  };

  const handleSubmit = async () => {
    if (!formData.city || !formData.state || !formData.income_band || !formData.pay_frequency) {
      toast.error('Please complete all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/onboarding`, formData, {
        withCredentials: true
      });
      
      toast.success('Profile completed!');
      navigate('/select-tier');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-background py-12 px-6">
      <div className="container mx-auto max-w-2xl">
        <div className="bg-card rounded-3xl shadow-soft p-8 md:p-12">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-3xl font-heading font-bold" data-testid="onboarding-title">
                Tell us about yourself
              </h1>
              <span className="text-sm text-muted-foreground">Step {step} of 2</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-primary rounded-full h-2 transition-all duration-300"
                style={{ width: `${(step / 2) * 100}%` }}
              ></div>
            </div>
          </div>

          {step === 1 && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city" data-testid="city-label">City</Label>
                  <Input
                    id="city"
                    data-testid="city-input"
                    placeholder="San Francisco"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="input-default mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="state" data-testid="state-label">State</Label>
                  <Input
                    id="state"
                    data-testid="state-input"
                    placeholder="CA"
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    className="input-default mt-2"
                  />
                </div>
              </div>

              <div>
                <Label data-testid="income-label">Monthly Income Range</Label>
                <Select 
                  value={formData.income_band} 
                  onValueChange={(value) => setFormData({...formData, income_band: value})}
                >
                  <SelectTrigger data-testid="income-select" className="input-default mt-2">
                    <SelectValue placeholder="Select your income range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under-2000">Under $2,000</SelectItem>
                    <SelectItem value="2000-3500">$2,000 - $3,500</SelectItem>
                    <SelectItem value="3500-5000">$3,500 - $5,000</SelectItem>
                    <SelectItem value="5000-7500">$5,000 - $7,500</SelectItem>
                    <SelectItem value="over-7500">Over $7,500</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label data-testid="pay-frequency-label">How often do you get paid?</Label>
                <Select 
                  value={formData.pay_frequency} 
                  onValueChange={(value) => setFormData({...formData, pay_frequency: value})}
                >
                  <SelectTrigger data-testid="pay-frequency-select" className="input-default mt-2">
                    <SelectValue placeholder="Select payment frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="bi-weekly">Bi-weekly (every 2 weeks)</SelectItem>
                    <SelectItem value="semi-monthly">Semi-monthly (twice a month)</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                data-testid="onboarding-next-btn"
                onClick={() => setStep(2)}
                className="btn-primary w-full mt-8"
                disabled={!formData.city || !formData.state || !formData.income_band || !formData.pay_frequency}
              >
                Continue <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <Label className="text-lg mb-4 block" data-testid="bills-label">
                  Which bills do you typically pay?
                </Label>
                <p className="text-sm text-muted-foreground mb-4">
                  Select all that apply. This helps us understand your needs better.
                </p>
                <div className="space-y-3">
                  {billOptions.map((bill) => (
                    <div key={bill.id} className="flex items-center space-x-3 p-4 rounded-xl border border-border hover:bg-muted/50 transition-colors">
                      <Checkbox
                        id={bill.id}
                        data-testid={`bill-${bill.id}-checkbox`}
                        checked={formData.typical_bills.includes(bill.id)}
                        onCheckedChange={() => handleBillToggle(bill.id)}
                      />
                      <label
                        htmlFor={bill.id}
                        className="text-base cursor-pointer flex-1"
                      >
                        {bill.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  data-testid="onboarding-back-btn"
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="btn-outline flex-1"
                >
                  Back
                </Button>
                <Button
                  data-testid="onboarding-complete-btn"
                  onClick={handleSubmit}
                  className="btn-primary flex-1"
                  disabled={loading || formData.typical_bills.length === 0}
                >
                  {loading ? 'Saving...' : 'Complete Profile'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
